import { rem } from 'polished'
import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  flightConfirmationCodeConfig,
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import type { CreateFlightItemViewModel } from '../Create/models'
import type { PatchFlightItemViewModel } from '../Edit/models'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import AirlineAutocomplete from 'tripPlanner/components/AutocompleteInputs/AirlineAutocomplete'
import AirportAutocomplete from 'tripPlanner/components/AutocompleteInputs/AirportAutocomplete'

const DividerContainer = styled.div`
  margin-top: ${rem(40)};
  height: ${rem(24)};
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};
`

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

interface Props {
  tripId: string
}

function FlightFields({ tripId }: Props) {
  const { airportConfig, urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateFlightItemViewModel | PatchFlightItemViewModel
  >()
  const { control, setValue, watch, register, getValues } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const watchBooked = watch('isBooked')

  const handleAirportOptionChange = useCallback(
    (place: 'origin' | 'destination', airport: App.AirportLocation) => {
      const placeType = place === 'origin' ? 'startPlace' : 'endPlace'

      if (place === 'origin') {
        setValue('originAirportCode', airport.airportCode)
      } else {
        setValue('destinationAirportCode', airport.airportCode)
      }

      if (airport) {
        if (airport.lt && airport.lg) {
          setValue(`${placeType}.type`, 'GEO')
          setValue(`${placeType}.point.lat`, airport.lt)
          setValue(`${placeType}.point.lng`, airport.lg)
        } else {
          setValue(`${placeType}.type`, 'CUSTOM')
        }

        setValue(`${placeType}.name`, airport.airportName)
        setValue(
          `${placeType}.address`,
          `${airport.cityAirportName}, ${airport.country}`,
        )
        setValue(
          `${placeType}.region`,
          `${airport.cityAirportName}, ${airport.country}`,
        )
      }
    },
    [setValue],
  )

  const handleDepartureChange = useCallback(
    (selectedOption: App.AirportLocation) => {
      handleAirportOptionChange('origin', selectedOption)
    },
    [handleAirportOptionChange],
  )

  const handleArrivalChange = useCallback(
    (selectedOption: App.AirportLocation) => {
      handleAirportOptionChange('destination', selectedOption)
    },
    [handleAirportOptionChange],
  )

  const airlineLabel = intl.formatMessage({
    id: 'wlh2Cjn/Jn',
    defaultMessage: 'Airline',
    description: 'Form label for airline name field in flight trip item editor',
  })

  const airlinePlaceholder = intl.formatMessage({
    id: 'EOcb59NRzf',
    defaultMessage: 'Search by airline name or code',
    description: 'Placeholder text for airline search input in flight trip item editor',
  })

  const flightNumberLabel = intl.formatMessage({
    id: 'x6Q2yd+eu9',
    defaultMessage: 'Flight number',
    description: 'Form label for flight number field in flight trip item editor',
  })

  const departureDetailsHeading = intl.formatMessage({
    id: 'T44wd6Cs7q',
    defaultMessage: 'Departure details',
    description: 'Section heading for departure details in flight trip item editor',
  })

  const airportLabel = intl.formatMessage({
    id: '32oDsLUjse',
    defaultMessage: 'Airport',
    description: 'Form label for airport field in flight trip item editor',
  })

  const airportPlaceholder = intl.formatMessage({
    id: '27gpqeDJmp',
    defaultMessage: 'Search by airport name or code',
    description: 'Placeholder text for airport search input in flight trip item editor',
  })

  const dateLabel = intl.formatMessage({
    id: '7iHTwF3yzu',
    defaultMessage: 'Date',
    description: 'Form label for date field in flight trip item editor',
  })

  const timeLabel = intl.formatMessage({
    id: 'dYlpVXE1eU',
    defaultMessage: 'Time',
    description: 'Form label for time field in flight trip item editor',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: 'o8l9g3dkr2',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in flight trip item editor',
  })

  const arrivalDetailsHeading = intl.formatMessage({
    id: 'DiuaIunk6l',
    defaultMessage: 'Arrival details',
    description: 'Section heading for arrival details in flight trip item editor',
  })

  const bookingReferenceLabel = intl.formatMessage({
    id: 'JGBAxEgbrV',
    defaultMessage: 'Booking reference',
    description: 'Form label for booking reference field in flight trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'qSaECgYUWu',
    defaultMessage: 'Price',
    description: 'Form label for price field in flight trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'sCk5RISH3I',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in flight trip item editor',
  })

  const noteLabel = intl.formatMessage({
    id: 'zlWhdClMMS',
    defaultMessage: 'Note',
    description: 'Form label for notes field in flight trip item editor',
  })

  return (
    <StyledVerticalSpacer gap={16}>
      <VerticalSpacer gap={32}>
        <AirlineAutocomplete
          {...register('airlineName')}
          label={airlineLabel}
          placeholder={airlinePlaceholder}
          initAirlineName={getValues('airlineName') ?? undefined}
        />

        <Input
          {...register('flightNumber')}
          label={flightNumberLabel}
          placeholder={flightNumberLabel}
        />

        <Heading variant="heading4">{departureDetailsHeading}</Heading>
      </VerticalSpacer>

      <VerticalSpacer gap={32}>
        <AirportAutocomplete
          {...register('originAirportCode')}
          label={airportLabel}
          placeholder={airportPlaceholder}
          required
          onAirportSelect={handleDepartureChange}
          noParentAirports
        />

        <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
          <RHFDatePickerInput
            name="startDate"
            label={dateLabel}
            defaultMonth={defaultMonth}
            defaultYear={defaultYear}
          />
          <TimeInput
            {...register('startTime')}
            label={timeLabel}
            placeholder={selectTimePlaceholder}
          />
        </Group>
      </VerticalSpacer>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Heading variant="heading4">{arrivalDetailsHeading}</Heading>

      <VerticalSpacer gap={32}>
        <AirportAutocomplete
          {...register('destinationAirportCode', { ...airportConfig })}
          label={airportLabel}
          placeholder={airportPlaceholder}
          required
          onAirportSelect={handleArrivalChange}
          noParentAirports
        />

        <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
          <RHFDatePickerInput
            name="endDate"
            label={dateLabel}
            defaultMonth={defaultMonth}
            defaultYear={defaultYear}
          />
          <TimeInput
            {...register('endTime')}
            label={timeLabel}
            placeholder={selectTimePlaceholder}
          />
        </Group>
      </VerticalSpacer>

      {/* <CheckboxField
        defaultChecked={getValues('isEndDateSame')}
        label="Arrival day is the same as departure"
        onClick={(e) => setValue('isEndDateSame', e.currentTarget.checked)}
      /> */}

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <Group
          direction="horizontal"
          horizontalAlign="stretch"
          verticalAlign="center"
          gap={16}
        >
          {/* <Controller
          name="fareClass"
          control={control}
          render={({ field }) => {
            return (
              <Select {...field} label="Cabin class">
                {fareCabins.map((opt) => (
                  <option key={opt.value} value={opt.value} label={opt.label}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )
          }}
        /> */}
          {watchBooked && (
            <Input
              {...register('confirmationCode', {
                ...flightConfirmationCodeConfig,
              })}
              label={bookingReferenceLabel}
              placeholder={bookingReferenceLabel}
            />
          )}
        </Group>
        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder="0.00"
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder="https://"
        />

        <TextArea
          {...register('notes')}
          placeholder="Add note here"
          label={noteLabel}
          size="tall"
          resize="vertical"
        />
      </ShowMore>
    </StyledVerticalSpacer>
  )
}

export default FlightFields
